/**
 * TREATX MFA Enrollment Page
 * ==========================
 * Consumed by privileged TreatX operator accounts on their first
 * post-deploy login (mandatory-MFA bootstrap). Reads the
 * intermediate `mfa_enroll_bootstrap` token from sessionStorage,
 * calls the canonical MFA endpoints and — on successful confirm —
 * the backend sets the final HttpOnly session cookies and returns
 * the workspace payload for immediate hand-off to the dashboard.
 *
 * Deliberately minimal: no redesign of Settings/Security. Frontend
 * NEVER stores the TOTP secret in localStorage; sessionStorage
 * holds the intermediate token for the duration of the enrollment
 * flow only.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { toast } from 'sonner';
import { persistCanonicalSessionFromMfa } from '../utils/finaliseMfaSession';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MfaEnrollPage() {
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  // TREATX-PROD-UAT-POST-MFA-REDIRECT-01 (2026-08): the enroll/confirm
  // response body is the canonical login payload (session + workspaces
  // + redirect_path). We must persist it into the same client-side
  // snapshot that AuthContext + RBACContext + SoftLaunchGate read on
  // boot; otherwise the reload lands on the public Soft Launch
  // landing even though the HttpOnly session cookie is set. See
  // `utils/finaliseMfaSession.js` for the exact key set and workspace
  // resolver — no parallel SSOT is introduced.
  const [postEnrollDestination, setPostEnrollDestination] = useState('/');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const mfaToken = (() => {
    try { return sessionStorage.getItem('treatx.mfa.token') || ''; } catch { return ''; }
  })();
  const accountEmail = (() => {
    try { return sessionStorage.getItem('treatx.mfa.email') || ''; } catch { return ''; }
  })();

  useEffect(() => {
    if (!mfaToken) {
      navigate('/login', { replace: true });
      return;
    }
    (async () => {
      setLoading(true);
      setError('');
      try {
        const r = await axios.post(`${API}/mfa/enroll/start`, {}, {
          headers: { Authorization: `Bearer ${mfaToken}` },
          withCredentials: true,
        });
        setEnrollment(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to start MFA enrollment');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirm = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/mfa/enroll/confirm`, { code }, {
        headers: { Authorization: `Bearer ${mfaToken}` },
        withCredentials: true,
      });
      setRecoveryCodes(r.data?.recovery_codes || []);
      // Persist the canonical session snapshot from the enroll/confirm
      // response body (identical shape to the login response) and
      // resolve the deterministic post-MFA destination. This closes
      // the "lands on Soft Launch landing after MFA enrollment"
      // production defect — without this the reload triggers the
      // gate with `isAuthenticated=false` because AuthContext reads
      // an empty `treatx_unified_user`.
      try {
        const dest = persistCanonicalSessionFromMfa(r.data?.session || null);
        if (dest) setPostEnrollDestination(dest);
      } catch { /* noop — the intermediate reload path below still works */ }
      try { sessionStorage.removeItem('treatx.mfa.token'); } catch { /* noop */ }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Verification failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const onContinue = () => {
    toast.success('Two-factor authentication enabled');
    window.location.hash = postEnrollDestination || '/';
    setTimeout(() => window.location.reload(), 50);
  };

  if (recoveryCodes) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
           data-testid="mfa-recovery-page">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow p-8">
          <h1 className="text-2xl font-semibold mb-2">Save your recovery codes</h1>
          <p className="text-sm text-slate-600 mb-4">
            Each code can be used once if you lose access to your authenticator.
            <strong> They are shown only this once.</strong>
          </p>
          <ul className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-100 rounded p-4 mb-6"
              data-testid="mfa-recovery-codes-list">
            {recoveryCodes.map((c) => <li key={c}>{c}</li>)}
          </ul>
          <button
            data-testid="mfa-recovery-continue-btn"
            onClick={onContinue}
            className="w-full rounded-lg bg-emerald-600 text-white py-2.5 font-medium hover:bg-emerald-700"
          >
            I have saved my codes — continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
         data-testid="mfa-enroll-page">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow p-8">
        <h1 className="text-2xl font-semibold mb-2">Set up two-factor authentication</h1>
        <p className="text-sm text-slate-600 mb-6">
          This TreatX account requires two-factor authentication (2FA).
          Scan the QR code with an authenticator app such as Google
          Authenticator, Microsoft Authenticator or 1Password, then
          enter the 6-digit code below.
        </p>
        {accountEmail && (
          <p className="text-xs text-slate-500 mb-4" data-testid="mfa-account-email">
            Signed in as {accountEmail}
          </p>
        )}
        {loading && !enrollment && (
          <p data-testid="mfa-loading" className="text-sm text-slate-500">Preparing your secret…</p>
        )}
        {enrollment && (
          <>
            <div className="flex justify-center my-4" data-testid="mfa-qr">
              <QRCodeSVG value={enrollment.provisioning_uri} size={192} />
            </div>
            <p className="text-xs text-slate-500 text-center mb-2">
              Or enter this setup key manually:
            </p>
            <p className="text-center font-mono text-sm bg-slate-100 rounded p-2 mb-6"
               data-testid="mfa-setup-key">
              {enrollment.secret}
            </p>
            <form onSubmit={onConfirm}>
              <label className="block text-sm font-medium mb-1">
                6-digit verification code
              </label>
              <input
                data-testid="mfa-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                pattern="[0-9]*"
                autoFocus
                className="w-full text-2xl tracking-widest text-center border rounded-lg py-2 mb-4"
                placeholder="000000"
              />
              {error && (
                <p className="text-sm text-red-600 mb-2" data-testid="mfa-enroll-error">{error}</p>
              )}
              <button
                data-testid="mfa-enroll-confirm-btn"
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full rounded-lg bg-emerald-600 text-white py-2.5 font-medium disabled:bg-slate-300 hover:bg-emerald-700"
              >
                {loading ? 'Verifying…' : 'Enable 2FA'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
